import React, { useState, useEffect, useRef } from 'react';
import { BrowserRouter as Router, Routes, Route, useNavigate, useLocation, Link } from 'react-router-dom';
import {
  signInWithPopup,
  signInWithRedirect,
  signOut as firebaseSignOut,
  onAuthStateChanged,
  getRedirectResult,
} from 'firebase/auth';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Sparkles, 
  FileText, 
  Upload, 
  Mic,
  Send, 
  Play, 
  Pause,
  Link as LinkIcon, 
  Copy, 
  Check, 
  ChevronLeft, 
  LogOut, 
  User,
  Clock,
  Eye,
  Volume2,
  MessageSquare,
  Loader2,
  X,
  Plus,
  Calendar,
  CheckCircle2,
  AlertCircle,
  Info,
  ArrowRight,
  Bell,
  Bot,
  Users,
  Shield,
  ExternalLink,
  RefreshCw
} from 'lucide-react';
import './App.css';
import LandingPage from './LandingPage';
import { auth, GoogleAuthProviderClass, isFirebaseConfigured, missingFirebaseEnvVars } from './firebase';
import { mergeFloat32Chunks, transcribePcm } from './whisperAsr';

const _apiBase = process.env.REACT_APP_API_URL || '/api';
const BACKEND_URL =
  _apiBase && _apiBase !== '/api' && !_apiBase.replace(/\/$/, '').endsWith('/api')
    ? _apiBase.replace(/\/$/, '') + '/api'
    : _apiBase.replace(/\/$/, '');
const BACKEND_ORIGIN = BACKEND_URL.replace(/\/api\/?$/, '') || '';

async function authFetch(url, options = {}, userOverride = null) {
  const isAuthMe = typeof url === 'string' && url.includes('/auth/me');
  const user = userOverride ?? (auth ? auth.currentUser : null);
  const forceRefresh = isAuthMe;

  const doFetch = async (refresh) => {
    const headers = { ...options.headers };
    if (user) {
      try {
        const token = await user.getIdToken(!!refresh);
        if (token) {
          headers['Authorization'] = `Bearer ${token}`;
          headers['X-Firebase-ID-Token'] = token;
        }
      } catch (_) {}
    }
    return fetch(url, { ...options, credentials: 'include', headers });
  };

  let response = await doFetch(forceRefresh);
  if (isAuthMe && response.status === 401 && user) {
    response = await doFetch(true);
  }
  return response;
}

function timeoutPromise(promise, ms, message) {
  let timerId = null;
  const timeout = new Promise((_, reject) => {
    timerId = setTimeout(() => reject(new Error(message)), ms);
  });
  return Promise.race([promise, timeout]).finally(() => {
    if (timerId) clearTimeout(timerId);
  });
}

/** Wall-clock position for audio UI (always mm:ss, even at 0). */
function formatAudioClock(seconds) {
  if (!Number.isFinite(seconds) || seconds < 0) return '0:00';
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${String(s).padStart(2, '0')}`;
}

/** Remove **markdown** bold markers from model text so chat reads as plain sentences. */
function stripMarkdownBold(text) {
  if (typeof text !== 'string') return text;
  return text.replace(/\*\*([^*]+?)\*\*/g, '$1').replace(/\*\*/g, '');
}

/** Drop trailing "(N words)" meta from executive summaries so it is not shown or read as narration. */
function stripExecutiveSummaryArtifacts(text) {
  if (typeof text !== 'string') return text;
  let s = text.trim();
  for (let i = 0; i < 6; i += 1) {
    const next = s.replace(/\s*\([^)]*\b\d{1,5}\s*words?\b[^)]*\)\s*$/i, '').trim();
    if (next === s) break;
    s = next;
  }
  const lines = s.split('\n');
  while (lines.length > 0) {
    const last = lines[lines.length - 1].trim();
    if (/^(?:approximately\s+)?\d{1,5}\s+words(?:\s+total)?\.?$/i.test(last)) {
      lines.pop();
      continue;
    }
    if (last.length < 100 && /\b\d{1,5}\s+words?\s+total\b/i.test(last)) {
      lines.pop();
      continue;
    }
    break;
  }
  return lines.join('\n').trim();
}

/** Finite duration from media element metadata or seekable range (helps streamed MP3). */
function readMediaDurationSeconds(mediaEl) {
  if (!mediaEl) return null;
  const d = mediaEl.duration;
  if (Number.isFinite(d) && d > 0) return d;
  try {
    if (mediaEl.seekable && mediaEl.seekable.length > 0) {
      const end = mediaEl.seekable.end(mediaEl.seekable.length - 1);
      if (Number.isFinite(end) && end > 0) return end;
    }
  } catch (_) {
    /* ignore */
  }
  return null;
}

function readBufferedEndSeconds(mediaEl) {
  if (!mediaEl?.buffered || mediaEl.buffered.length === 0) return 0;
  try {
    return mediaEl.buffered.end(mediaEl.buffered.length - 1);
  } catch (_) {
    return 0;
  }
}

/**
 * Range slider max when duration is unknown (no Content-Length / metadata yet).
 * Grows with buffered bytes and playback position so the thumb can track audio.
 */
function computeScrubMaxSeconds(mediaEl, currentTime, knownDuration) {
  if (Number.isFinite(knownDuration) && knownDuration > 0) return knownDuration;
  let fromSeekable = 0;
  try {
    if (mediaEl?.seekable && mediaEl.seekable.length > 0) {
      fromSeekable = mediaEl.seekable.end(mediaEl.seekable.length - 1);
    }
  } catch (_) {
    /* ignore */
  }
  const buf = readBufferedEndSeconds(mediaEl);
  const ct = Number.isFinite(currentTime) && currentTime >= 0 ? currentTime : 0;
  return Math.max(fromSeekable, buf, ct + 0.5, 1);
}

// ==================== Auth Callback Component ====================
function AuthCallback() {
  const navigate = useNavigate();
  useEffect(() => {
    navigate('/login', { replace: true });
  }, [navigate]);
  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50">
      <div className="text-slate-600 text-xl flex items-center space-x-3">
        <Loader2 className="w-6 h-6 animate-spin" />
        <span>Redirecting...</span>
      </div>
    </div>
  );
}

// ==================== Login Page ====================
function LoginPage() {
  const navigate = useNavigate();
  const [checking, setChecking] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const checkPerformed = useRef(false);

  useEffect(() => {
    if (checkPerformed.current) return;

    const checkAuth = async (firebaseUser) => {
      try {
        const response = await authFetch(`${BACKEND_URL}/auth/me`, {}, firebaseUser ?? undefined);
        if (response.ok) {
          const userData = await response.json();
          navigate('/dashboard', { replace: true, state: { user: userData } });
          return;
        }

        if (firebaseUser) {
          const idToken = await firebaseUser.getIdToken(true);
          const res = await authFetch(
            `${BACKEND_URL}/auth/session`,
            {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ id_token: idToken }),
            },
            firebaseUser
          );
          if (res.ok) {
            const userData = await res.json();
            navigate('/dashboard', { replace: true, state: { user: userData } });
            return;
          }
        }
      } catch (_) {}
      setChecking(false);
    };

    let unsub = null;

    if (!auth) {
      checkPerformed.current = true;
      setChecking(false);
      return undefined;
    }

    const init = async () => {
      try {
        await getRedirectResult(auth);
      } catch (_) {}

      unsub = onAuthStateChanged(auth, async (firebaseUser) => {
        if (checkPerformed.current) return;
        if (!firebaseUser) {
          checkPerformed.current = true;
          setChecking(false);
          return;
        }
        checkPerformed.current = true;
        await checkAuth(firebaseUser);
      });
    };

    init();
    return () => {
      if (unsub) unsub();
    };
  }, [navigate]);

  const handleLogin = async () => {
    if (!isFirebaseConfigured) {
      setError('Firebase is not configured. Add REACT_APP_FIREBASE_* to .env.local');
      return;
    }
    setError(null);
    setLoading(true);
    let didRedirect = false;
    try {
      const provider = new GoogleAuthProviderClass();
      const result = await timeoutPromise(
        signInWithPopup(auth, provider),
        12000,
        'Google sign-in timed out. Please try again.'
      );
      const idToken = await result.user.getIdToken();

      const controller = new AbortController();
      const abortTimer = setTimeout(() => controller.abort(), 20000);
      const res = await authFetch(`${BACKEND_URL}/auth/session`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id_token: idToken }),
        signal: controller.signal,
      });
      clearTimeout(abortTimer);

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Sign-in failed');
      }
      const user = await res.json();
      navigate('/dashboard', { replace: true, state: { user } });
    } catch (e) {
      const msg = e?.message || String(e || '');
      const shouldRedirect =
        msg.toLowerCase().includes('timed out') ||
        msg.includes('Cross-Origin-Opener-Policy') ||
        msg.includes('window.close') ||
        msg.includes('window.closed');

      if (shouldRedirect) {
        didRedirect = true;
        try {
          await signInWithRedirect(auth, new GoogleAuthProviderClass());
          return;
        } catch (redirErr) {
          setError(redirErr?.message || 'Sign-in failed (redirect)');
          return;
        }
      }
      setError(msg || 'Sign-in failed');
    } finally {
      if (!didRedirect) setLoading(false);
    }
  };

  if (checking) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-slate-600 flex items-center space-x-3">
          <Loader2 className="w-6 h-6 animate-spin text-primary-600" />
          <span>Checking authentication...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-primary-50 flex items-center justify-center px-4">
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md"
      >
        <div className="glass-card p-8">
          <div className="text-center mb-8">
            <div className="flex items-center justify-center space-x-3 mb-6">
              <div className="w-12 h-12 bg-gradient-to-br from-primary-500 to-primary-700 rounded-xl flex items-center justify-center shadow-lg shadow-primary-500/25">
                <Sparkles className="w-6 h-6 text-white" />
              </div>
              <span className="text-3xl font-bold text-slate-900">Converse</span>
            </div>
            <h1 className="text-2xl font-bold text-slate-900 mb-2">Continue to the demo</h1>
            <p className="text-slate-600">
              Sign in with Google to explore the preview—upload a report and try voice briefings and Q&amp;A.
            </p>
          </div>

          <div
            className="mb-6 rounded-2xl border border-primary-200/70 bg-gradient-to-br from-primary-50/90 to-white/80 px-4 py-4 sm:px-5 sm:py-5 text-left shadow-sm"
            role="status"
          >
            <div className="flex gap-3.5">
              <div
                className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-white/90 text-primary-600 shadow-sm ring-1 ring-primary-100"
                aria-hidden
              >
                <Info className="h-5 w-5" strokeWidth={2} />
              </div>
              <div className="min-w-0 space-y-2 pt-0.5">
                <p className="text-xs font-semibold text-primary-800 tracking-wide">Proof-of-concept preview</p>
                <p className="text-sm text-slate-600 leading-relaxed">
                  What you see here is an early demo with{' '}
                  <span className="font-medium text-slate-800">intentionally limited features</span>
                  —enough to validate the experience while we build the full platform.
                </p>
                <p className="text-sm text-slate-600 leading-relaxed">
                  Want the complete product first? Join the waitlist and we&apos;ll reach out as soon as early access
                  opens.
                </p>
                <Link
                  to="/waitlist"
                  className="inline-flex items-center gap-1.5 pt-1 text-sm font-semibold text-primary-700 hover:text-primary-800 transition-colors rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2"
                >
                  Join the waitlist
                  <ArrowRight className="h-4 w-4 shrink-0" aria-hidden />
                </Link>
              </div>
            </div>
          </div>

          <div className="space-y-6">
            {error && (
              <motion.div 
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                className="flex items-start space-x-3 text-sm text-red-600 bg-red-50 border border-red-200 p-4 rounded-xl"
              >
                <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
                <span>{error}</span>
              </motion.div>
            )}

            {!isFirebaseConfigured && (
              <div className="text-sm text-amber-800 bg-amber-50 border border-amber-200 p-4 rounded-xl space-y-2">
                <p className="font-medium">Firebase isn’t available in this build.</p>
                <p className="text-amber-900/90">
                  Create React App bakes <code className="text-xs bg-amber-100/80 px-1 rounded">REACT_APP_*</code> in at{' '}
                  <strong>build time</strong>. If you added vars in Vercel after the last deploy, run{' '}
                  <strong>Redeploy</strong> (or push a new commit). For preview URLs, add the same vars for{' '}
                  <strong>Preview</strong> (not only Production).
                </p>
                {missingFirebaseEnvVars.length > 0 && (
                  <div>
                    <p className="text-xs font-semibold text-amber-900 mb-1">Missing or empty in this build:</p>
                    <ul className="text-xs font-mono text-amber-900/90 list-disc list-inside space-y-0.5">
                      {missingFirebaseEnvVars.map((name) => (
                        <li key={name}>{name}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}

            <button
              onClick={handleLogin}
              disabled={loading}
              data-testid="google-login-btn"
              className="w-full bg-white border-2 border-slate-200 text-slate-700 font-semibold py-4 px-6 rounded-xl hover:bg-slate-50 hover:border-slate-300 transition-all flex items-center justify-center space-x-3 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              <span>{loading ? 'Signing in...' : 'Continue with Google'}</span>
              {loading && <Loader2 className="w-4 h-4 animate-spin" />}
            </button>
          </div>

          <p className="mt-8 text-center text-sm text-slate-500">
            By signing in, you agree to our Terms and Privacy Policy
          </p>
        </div>

        <p className="text-center mt-6 text-sm text-slate-500">
          <button
            type="button"
            onClick={() => navigate('/')}
            className="text-primary-600 hover:text-primary-700 font-medium rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2"
          >
            ← Back to home
          </button>
        </p>
      </motion.div>
    </div>
  );
}

// ==================== Waitlist Page ====================
function WaitlistPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const fromLimit = location.state?.fromLimit;
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/waitlist`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim(), name: name.trim() || undefined }),
      });
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).detail || 'Failed');
      setDone(true);
    } catch (e) {
      setError(e.message || 'Something went wrong');
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-primary-50 flex items-center justify-center px-4 py-12">
        <motion.div
          initial={{ opacity: 0, scale: 0.97 }}
          animate={{ opacity: 1, scale: 1 }}
          className="glass-card p-8 sm:p-10 max-w-md w-full text-center shadow-lg shadow-slate-200/50"
        >
          <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-5">
            <CheckCircle2 className="w-8 h-8 text-emerald-600" aria-hidden />
          </div>
          <h1 className="text-2xl font-bold text-slate-900 mb-2">You&apos;re on the list</h1>
          <p className="text-slate-600 leading-relaxed mb-2">
            Thanks for raising your hand. We&apos;ll email you when early access opens for the full Converse AI
            platform—beyond what today&apos;s proof-of-concept demo can show.
          </p>
          <p className="text-sm text-slate-500 mb-8">Watch your inbox. We won&apos;t spam you.</p>
          <button
            type="button"
            onClick={() => navigate('/')}
            className="btn-primary rounded-xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2"
          >
            Back to home
          </button>
        </motion.div>
      </div>
    );
  }

  const highlights = [
    {
      id: 'voice',
      icon: FileText,
      text: (
        <>
          <span className="font-medium text-slate-800">Voice briefings</span> turn dense PDFs into short, listenable
          summaries—so busy leaders actually hear your narrative.
        </>
      ),
    },
    {
      id: 'agent',
      icon: Bot,
      text: (
        <>
          <span className="font-medium text-slate-800">Conversational agent</span> for live follow-ups by voice or
          chat—replies stay grounded in your report, so nothing feels like a random AI guess.
        </>
      ),
    },
    {
      id: 'personalized',
      icon: Users,
      text: (
        <>
          <span className="font-medium text-slate-800">Customised briefings</span> adapt to role and audience: the CFO,
          Ops Lead, and Board Members each get the angle that matters to them.
        </>
      ),
    },
    {
      id: 'trust',
      icon: Shield,
      text: (
        <>
          <span className="font-medium text-slate-800">Built with Security in Mind</span>—structured for sensitive reporting
          workflows so leadership can explore the story without losing control of the source material.
        </>
      ),
    },
    {
      id: 'early',
      icon: Bell,
      text: (
        <>
          <span className="font-medium text-slate-800">Early access</span> puts waitlist members first when we ship the
          full platform beyond today&apos;s limited demo.
        </>
      ),
    },
  ];

  return (
    <div className="relative min-h-screen overflow-hidden bg-gradient-to-br from-slate-50 via-white to-primary-50">
      <div
        className="pointer-events-none absolute inset-0"
        aria-hidden
      >
        <div className="absolute -top-24 right-0 h-80 w-80 rounded-full bg-primary-200/25 blur-3xl md:right-[10%]" />
        <div className="absolute bottom-0 left-0 h-72 w-72 rounded-full bg-emerald-200/20 blur-3xl md:left-[5%]" />
      </div>

      <div className="relative mx-auto flex min-h-screen max-w-6xl flex-col justify-center px-4 py-10 sm:px-6 lg:px-8 lg:py-16">
        <div className="grid w-full items-start gap-10 lg:grid-cols-12 lg:items-stretch lg:gap-12 xl:gap-16">
          {/* Form column first on mobile — fewer scrolls to the fields */}
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.35 }}
            className="order-1 w-full lg:order-2 lg:col-span-5 xl:col-span-5 lg:self-center lg:justify-self-stretch"
          >
            <div className="glass-card border-white/70 p-6 shadow-lg shadow-slate-200/35 sm:p-8">
              <div className="mb-6 flex items-center gap-3">
                <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-primary-500 to-primary-700 shadow-md shadow-primary-500/20">
                  <Sparkles className="h-5 w-5 text-white" aria-hidden />
                </div>
                <div className="min-w-0">
                  <p className="text-xs font-semibold uppercase tracking-wide text-primary-700">Converse AI</p>
                  <p className="truncate text-lg font-bold text-slate-900">Join the waitlist</p>
                </div>
              </div>
              <p className="mb-6 text-sm leading-relaxed text-slate-600 lg:hidden">
                Early access to voice briefings and document-grounded Q&amp;A—beyond today&apos;s limited demo.
              </p>

              {fromLimit && (
                <div
                  className="mb-5 rounded-xl border border-amber-200/90 bg-amber-50/90 px-3.5 py-3 text-sm text-amber-950"
                  role="status"
                >
                  <p className="font-medium text-amber-900">Demo limit reached</p>
                  <p className="mt-1 leading-snug text-amber-900/90">
                    Drop your email—we&apos;ll notify you when you can do more.
                  </p>
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-4">
                {error && (
                  <div className="rounded-xl border border-red-200/80 bg-red-50 p-3 text-sm text-red-700">{error}</div>
                )}
                <div>
                  <label htmlFor="waitlist-email" className="mb-1.5 block text-sm font-medium text-slate-700">
                    Work email <span className="text-red-600">*</span>
                  </label>
                  <input
                    id="waitlist-email"
                    type="email"
                    required
                    autoComplete="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="input-field w-full"
                    placeholder="you@company.com"
                  />
                </div>
                <div>
                  <label htmlFor="waitlist-name" className="mb-1.5 block text-sm font-medium text-slate-700">
                    Name <span className="font-normal text-slate-400">(optional)</span>
                  </label>
                  <input
                    id="waitlist-name"
                    type="text"
                    autoComplete="name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="input-field w-full"
                    placeholder="Your name"
                  />
                </div>
                <button
                  type="submit"
                  disabled={loading}
                  className="btn-primary flex w-full items-center justify-center gap-2 rounded-xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2 disabled:opacity-60"
                >
                  {loading ? (
                    <>
                      <Loader2 className="h-4 w-4 shrink-0 animate-spin" aria-hidden />
                      <span>Submitting…</span>
                    </>
                  ) : (
                    <>
                      <span>Join the waitlist</span>
                      <ArrowRight className="h-4 w-4 shrink-0" aria-hidden />
                    </>
                  )}
                </button>
              </form>

              <p className="mt-4 text-center text-xs leading-relaxed text-slate-500">
                Product updates &amp; early access only—no spam.
              </p>

              <p className="mt-5 text-center">
                <button
                  type="button"
                  onClick={() => navigate(-1)}
                  className="text-sm font-medium text-primary-600 transition-colors hover:text-primary-700 rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2"
                >
                  ← Go back
                </button>
              </p>
            </div>
          </motion.div>

          {/* Story column — uses horizontal space on large screens; below form on mobile */}
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.05 }}
            className="order-2 lg:order-1 lg:col-span-7 xl:col-span-7 space-y-6 lg:space-y-8 lg:pr-4"
          >
            <div>
              <p className="text-xs font-semibold uppercase tracking-wider text-primary-700">Early access</p>
              <h1 className="mt-2 text-3xl font-bold leading-tight tracking-tight text-slate-900 sm:text-4xl lg:text-[2.35rem] lg:leading-[1.15]">
                Why are people joining the list?
              </h1>
              <p className="mt-4 max-w-xl text-base leading-relaxed text-slate-600 sm:text-lg">
                Converse AI turns board packs and reports into{' '}
                <span className="font-medium text-slate-800">spoken briefings and live Q&amp;A</span>—so decisions aren’t
                made from half-read decks.
              </p>
              <div
                className="mt-4 flex max-w-xl gap-3 rounded-xl border border-primary-200/80 bg-gradient-to-br from-primary-50/90 to-white/70 px-4 py-3.5 shadow-sm"
                role="note"
              >
                <Info className="mt-0.5 h-5 w-5 shrink-0 text-primary-600" strokeWidth={2} aria-hidden />
                <p className="text-sm leading-relaxed text-slate-700 sm:text-[15px]">
                  <span className="font-semibold text-slate-900">
                    The public demo is a proof of concept with limited features.
                  </span>{' '}
                  Waitlist members are first in line for the full product when we ship it.
                </p>
              </div>
            </div>

            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-6 lg:gap-4">
              {highlights.map(({ id, icon: Icon, text }, index) => (
                <div
                  key={id}
                  className={`rounded-2xl border border-slate-200/90 bg-white/55 px-4 py-3.5 shadow-sm backdrop-blur-sm sm:py-4 ${
                    index < 3 ? 'lg:col-span-2' : 'lg:col-span-3'
                  } ${index === 4 ? 'sm:col-span-2 sm:max-w-md sm:justify-self-center' : ''}`}
                >
                  <Icon className="mb-2 h-5 w-5 text-primary-600" strokeWidth={2} aria-hidden />
                  <p className="text-sm leading-snug text-slate-600">{text}</p>
                </div>
              ))}
            </div>

            <p className="max-w-lg text-sm text-slate-500">
              Prefer to try first?{' '}
              <button
                type="button"
                onClick={() => navigate('/login')}
                className="font-semibold text-primary-600 hover:text-primary-700 rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2"
              >
                Open the demo
              </button>
              {' '}—then come back here when you want the complete experience.
            </p>
          </motion.div>
        </div>
      </div>
    </div>
  );
}

const WAITLIST_ADMIN_STORAGE_KEY = 'converse_waitlist_admin_secret';

// ==================== Waitlist admin (secret header; configure server WAITLIST_ADMIN_SECRET) ====================
function WaitlistAdminPage() {
  const navigate = useNavigate();
  const [secret, setSecret] = useState(() => sessionStorage.getItem(WAITLIST_ADMIN_STORAGE_KEY) || '');
  const [inputSecret, setInputSecret] = useState('');
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(() => Boolean(sessionStorage.getItem(WAITLIST_ADMIN_STORAGE_KEY)));
  const [error, setError] = useState(null);
  const [unlocked, setUnlocked] = useState(() => Boolean(sessionStorage.getItem(WAITLIST_ADMIN_STORAGE_KEY)));

  const fetchList = async (key) => {
    setError(null);
    setLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/admin/waitlist`, {
        headers: { 'X-Waitlist-Admin-Secret': key },
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(
          typeof data.detail === 'string' ? data.detail : JSON.stringify(data.detail) || res.statusText || 'Request failed'
        );
      }
      setEntries(data.entries || []);
      setUnlocked(true);
      sessionStorage.setItem(WAITLIST_ADMIN_STORAGE_KEY, key);
      setSecret(key);
    } catch (e) {
      setError(e.message || 'Failed to load');
      setUnlocked(false);
      sessionStorage.removeItem(WAITLIST_ADMIN_STORAGE_KEY);
      setSecret('');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const stored = sessionStorage.getItem(WAITLIST_ADMIN_STORAGE_KEY);
    if (stored) {
      fetchList(stored);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleUnlock = (e) => {
    e.preventDefault();
    const key = inputSecret.trim();
    if (!key) return;
    fetchList(key);
  };

  const handleLock = () => {
    sessionStorage.removeItem(WAITLIST_ADMIN_STORAGE_KEY);
    setSecret('');
    setInputSecret('');
    setEntries([]);
    setUnlocked(false);
    setError(null);
  };

  const formatDate = (iso) => {
    if (!iso) return '—';
    try {
      return new Date(iso).toLocaleString(undefined, {
        dateStyle: 'medium',
        timeStyle: 'short',
      });
    } catch (_) {
      return String(iso);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-100 px-4 py-10 sm:px-6">
      <div className="mx-auto max-w-5xl">
        <div className="mb-8 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-slate-900 text-white">
              <Shield className="h-5 w-5" aria-hidden />
            </div>
            <div>
              <h1 className="text-xl font-bold text-slate-900">Waitlist admin</h1>
              <p className="text-sm text-slate-500">Entries stored in Firestore (via API)</p>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            {unlocked && (
              <>
                <button
                  type="button"
                  onClick={() => secret && fetchList(secret)}
                  disabled={loading}
                  className="rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"
                >
                  Refresh
                </button>
                <button
                  type="button"
                  onClick={handleLock}
                  className="rounded-lg border border-red-200 bg-red-50 px-4 py-2 text-sm font-medium text-red-800 hover:bg-red-100"
                >
                  Lock / clear session
                </button>
              </>
            )}
            <button
              type="button"
              onClick={() => navigate('/')}
              className="rounded-lg px-4 py-2 text-sm font-medium text-primary-700 hover:text-primary-800"
            >
              Home
            </button>
          </div>
        </div>

        {!unlocked && (
          <div className="glass-card mx-auto max-w-md p-6 shadow-md">
            <p className="text-sm leading-relaxed text-slate-600 mb-4">
              Enter the admin secret configured as <code className="text-xs">WAITLIST_ADMIN_SECRET</code> on your
              backend. It is not stored in the app build—only in this browser session after you unlock.
            </p>
            <form onSubmit={handleUnlock} className="space-y-4">
              {error && (
                <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">{error}</div>
              )}
              <div>
                <label htmlFor="admin-secret" className="mb-1 block text-sm font-medium text-slate-700">
                  Admin secret
                </label>
                <input
                  id="admin-secret"
                  type="password"
                  autoComplete="off"
                  value={inputSecret}
                  onChange={(e) => setInputSecret(e.target.value)}
                  className="input-field w-full"
                  placeholder="From server env only"
                />
              </div>
              <button type="submit" disabled={loading} className="btn-primary w-full rounded-xl">
                {loading ? 'Checking…' : 'Unlock'}
              </button>
            </form>
          </div>
        )}

        {unlocked && !loading && entries.length === 0 && !error && (
          <p className="text-center text-slate-600">No entries yet.</p>
        )}

        {unlocked && entries.length > 0 && (
          <div className="glass-card overflow-hidden shadow-md">
            <div className="overflow-x-auto">
              <table className="w-full min-w-[600px] text-left text-sm">
                <thead>
                  <tr className="border-b border-slate-200 bg-slate-50/80">
                    <th className="px-4 py-3 font-semibold text-slate-700">Email</th>
                    <th className="px-4 py-3 font-semibold text-slate-700">Name</th>
                    <th className="px-4 py-3 font-semibold text-slate-700">Joined</th>
                    <th className="px-4 py-3 font-semibold text-slate-700 text-xs font-normal text-slate-500">ID</th>
                  </tr>
                </thead>
                <tbody>
                  {entries.map((row) => (
                    <tr key={row.id} className="border-b border-slate-100 hover:bg-slate-50/50">
                      <td className="px-4 py-3 font-medium text-slate-900">{row.email || '—'}</td>
                      <td className="px-4 py-3 text-slate-700">{row.name || '—'}</td>
                      <td className="px-4 py-3 text-slate-600 whitespace-nowrap">{formatDate(row.createdAt)}</td>
                      <td className="px-4 py-3 font-mono text-xs text-slate-400 break-all max-w-[140px]">{row.id}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="border-t border-slate-100 px-4 py-2 text-xs text-slate-500">{entries.length} total</p>
          </div>
        )}

        {unlocked && loading && (
          <div className="flex justify-center py-12 text-slate-600">
            <Loader2 className="h-8 w-8 animate-spin text-primary-600" aria-hidden />
          </div>
        )}
      </div>
    </div>
  );
}

// ==================== Protected Route ====================
function ProtectedRoute({ children }) {
  const [isAuthenticated, setIsAuthenticated] = useState(null);
  const [user, setUser] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  const checkPerformed = useRef(false);

  useEffect(() => {
    if (location.state?.user) {
      setUser(location.state.user);
      setIsAuthenticated(true);
      checkPerformed.current = true;
      return;
    }

    if (checkPerformed.current) return;

    const checkAuth = async (firebaseUser) => {
      if (checkPerformed.current) return;
      checkPerformed.current = true;
      try {
        const response = await authFetch(`${BACKEND_URL}/auth/me`, {}, firebaseUser ?? undefined);
        if (!response.ok) {
          setIsAuthenticated(false);
          navigate('/login', { replace: true });
          return;
        }
        const userData = await response.json();
        setUser(userData);
        setIsAuthenticated(true);
      } catch (error) {
        setIsAuthenticated(false);
        navigate('/login', { replace: true });
      }
    };

    if (!auth) {
      checkAuth(null);
      return;
    }
    const unsub = onAuthStateChanged(auth, (firebaseUser) => {
      if (checkPerformed.current) return;
      if (!firebaseUser) {
        checkPerformed.current = true;
        setIsAuthenticated(false);
        navigate('/login', { replace: true });
        return;
      }
      checkAuth(firebaseUser);
    });
    return () => unsub();
  }, [location.state?.user, navigate]);

  if (isAuthenticated === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return null;
  }

  return React.cloneElement(children, { user });
}

// ==================== Dashboard Page ====================
function DashboardPage({ user }) {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    loadReports();
  }, []);

  const loadReports = async () => {
    try {
      const response = await authFetch(`${BACKEND_URL}/reports`);
      const data = await response.json();
      setReports(data);
    } catch (error) {
      console.error('Error loading reports:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    const file = formData.get('file');
    const maxSize = 5 * 1024 * 1024;
    if (file && file.size > maxSize) {
      alert(`File is too large. Maximum size is ${maxSize / 1024 / 1024} MB.`);
      return;
    }

    setUploading(true);
    try {
      const response = await authFetch(`${BACKEND_URL}/reports/upload`, {
        method: 'POST',
        body: formData
      });

      if (response.ok) {
        setShowUploadModal(false);
        loadReports();
        e.target.reset();
      } else if (response.status === 403) {
        const data = await response.json().catch(() => ({}));
        const detail = typeof data.detail === 'object' ? data.detail : {};
        if (detail.code === 'REPORT_LIMIT_REACHED') {
          setShowUploadModal(false);
          navigate('/waitlist', { state: { fromLimit: true } });
          return;
        }
        alert(detail.message || 'Upload limit reached.');
      } else {
        const data = await response.json().catch(() => ({}));
        const msg = typeof data.detail === 'object' ? data.detail?.message : data.detail;
        alert(msg || 'Upload failed');
      }
    } catch (error) {
      console.error('Upload error:', error);
      alert('Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await authFetch(`${BACKEND_URL}/auth/logout`, { method: 'POST' });
      if (auth) await firebaseSignOut(auth);
      navigate('/login');
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="glass-surface sticky top-0 z-40 border-b border-slate-200/60">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-gradient-to-br from-primary-500 to-primary-700 rounded-xl flex items-center justify-center shadow-lg shadow-primary-500/25">
                  <Sparkles className="w-5 h-5 text-white" />
                </div>
                <span className="text-xl font-bold text-slate-900">Converse</span>
              </div>
              <div className="hidden sm:block h-6 w-px bg-slate-200" />
              <span className="hidden sm:block text-sm font-medium text-slate-500">User Dashboard</span>
            </div>
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-3 px-4 py-2 bg-white rounded-xl border border-slate-200">
                <div className="w-8 h-8 bg-primary-100 rounded-lg flex items-center justify-center">
                  <User className="w-4 h-4 text-primary-600" />
                </div>
                <span className="text-sm font-medium text-slate-700 hidden sm:block">{user?.name}</span>
              </div>
              <button
                onClick={handleLogout}
                data-testid="logout-btn"
                className="p-2 text-slate-500 hover:text-slate-700 hover:bg-slate-100 rounded-lg transition"
                title="Logout"
              >
                <LogOut className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Page Title & Upload Button */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Your Reports</h1>
            <p className="text-slate-600 mt-1">Upload reports and create AI-powered briefings</p>
          </div>
          <button
            onClick={() => setShowUploadModal(true)}
            data-testid="upload-report-btn"
            className="btn-primary flex items-center space-x-2"
          >
            <Plus className="w-5 h-5" />
            <span>Upload Report</span>
          </button>
        </div>

        {/* Reports Grid */}
        {loading ? (
          <div className="flex justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
          </div>
        ) : reports.length === 0 ? (
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center py-20"
          >
            <div className="w-20 h-20 bg-slate-100 rounded-2xl flex items-center justify-center mx-auto mb-6">
              <FileText className="w-10 h-10 text-slate-400" />
            </div>
            <h3 className="text-xl font-semibold text-slate-900 mb-2">No reports yet</h3>
            <p className="text-slate-600 mb-6">Upload your first report to get started with AI briefings</p>
            <button onClick={() => setShowUploadModal(true)} className="btn-primary">
              Upload Your First Report
            </button>
          </motion.div>
        ) : (
          <motion.div 
            initial="hidden"
            animate="visible"
            variants={{
              visible: { transition: { staggerChildren: 0.1 } }
            }}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
          >
            {reports.map((report) => (
              <motion.div
                key={report.report_id}
                variants={{
                  hidden: { opacity: 0, y: 20 },
                  visible: { opacity: 1, y: 0 }
                }}
                data-testid={`report-card-${report.report_id}`}
                className="glass-card glass-card-hover p-6 cursor-pointer group"
                onClick={() => navigate(`/report/${report.report_id}`)}
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="w-12 h-12 bg-primary-100 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform">
                    <FileText className="w-6 h-6 text-primary-600" />
                  </div>
                  <div className={`px-3 py-1 rounded-full text-xs font-medium ${
                    report.summary 
                      ? 'bg-emerald-100 text-emerald-700' 
                      : 'bg-amber-100 text-amber-700'
                  }`}>
                    {report.summary ? 'Ready' : 'Pending'}
                  </div>
                </div>
                <h3 className="text-lg font-semibold text-slate-900 mb-2 line-clamp-2">{report.title}</h3>
                <div className="flex items-center space-x-4 text-sm text-slate-500">
                  <span className="flex items-center space-x-1">
                    <Calendar className="w-4 h-4" />
                    <span>{new Date(report.created_at).toLocaleDateString()}</span>
                  </span>
                </div>
                <div className="mt-4 pt-4 border-t border-slate-100">
                  {report.summary ? (
                    <div className="flex items-center space-x-2 text-emerald-600 text-sm font-medium">
                      <CheckCircle2 className="w-4 h-4" />
                      <span>AI briefing ready</span>
                    </div>
                  ) : (
                    <div className="flex items-center space-x-2 text-amber-600 text-sm font-medium">
                      <Clock className="w-4 h-4" />
                      <span>Generate briefing to share</span>
                    </div>
                  )}
                </div>
              </motion.div>
            ))}
          </motion.div>
        )}
      </main>

      {/* Upload Modal */}
      <AnimatePresence>
        {showUploadModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm flex items-center justify-center p-4 z-50"
            onClick={() => setShowUploadModal(false)}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className="glass-card max-w-md w-full p-6"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-slate-900">Upload Report</h2>
                <button 
                  onClick={() => setShowUploadModal(false)}
                  className="p-2 hover:bg-slate-100 rounded-lg transition"
                >
                  <X className="w-5 h-5 text-slate-500" />
                </button>
              </div>
              <form onSubmit={handleUpload} className="space-y-5">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Report Title
                  </label>
                  <input
                    type="text"
                    name="title"
                    data-testid="report-title-input"
                    required
                    className="input-field w-full"
                    placeholder="Q4 Executive Summary"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    PDF File (max 5 MB)
                  </label>
                  <div className="border-2 border-dashed border-slate-200 rounded-xl p-6 text-center hover:border-primary-300 transition-colors">
                    <Upload className="w-8 h-8 text-slate-400 mx-auto mb-2" />
                    <input
                      type="file"
                      name="file"
                      data-testid="report-file-input"
                      accept=".pdf"
                      required
                      className="w-full text-sm text-slate-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-primary-50 file:text-primary-700 hover:file:bg-primary-100"
                    />
                    <p className="text-xs text-slate-500 mt-2">PDF files only</p>
                  </div>
                </div>
                <div className="flex space-x-3 pt-2">
                  <button
                    type="button"
                    onClick={() => setShowUploadModal(false)}
                    disabled={uploading}
                    className="btn-secondary flex-1"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    data-testid="upload-submit-btn"
                    disabled={uploading}
                    className="btn-primary flex-1 flex items-center justify-center space-x-2"
                  >
                    {uploading ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        <span>Uploading...</span>
                      </>
                    ) : (
                      <>
                        <Upload className="w-4 h-4" />
                        <span>Upload</span>
                      </>
                    )}
                  </button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// ==================== Report Detail Page ====================
function ReportDetailPage({ user }) {
  const { reportId } = useLocation().pathname.match(/\/report\/(?<reportId>[^/]+)/)?.groups || {};
  const [report, setReport] = useState(null);
  const [links, setLinks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [creatingLink, setCreatingLink] = useState(false);
  const [reextracting, setReextracting] = useState(false);
  const [copiedLinkId, setCopiedLinkId] = useState(null);
  const navigate = useNavigate();
  const narrationAudioRef = useRef(null);
  const narrationSeekingRef = useRef(false);
  const [narrationDurationSec, setNarrationDurationSec] = useState(null);
  const [narrationScrubMax, setNarrationScrubMax] = useState(1);
  const [narrationCurrentTime, setNarrationCurrentTime] = useState(0);
  const [narrationIsPlaying, setNarrationIsPlaying] = useState(false);
  const [narrationPlaybackRate, setNarrationPlaybackRate] = useState(1);

  const safeDurationSeconds = (seconds) => {
    return Number.isFinite(seconds) && seconds > 0 ? seconds : null;
  };

  const formatNarrationMinutes = (seconds) => {
    const safe = safeDurationSeconds(seconds);
    if (!safe) return null;
    // Round to nearest minute for simple UX.
    return Math.max(1, Math.round(safe / 60));
  };

  useEffect(() => {
    if (narrationAudioRef.current) {
      narrationAudioRef.current.playbackRate = narrationPlaybackRate;
    }
  }, [narrationPlaybackRate]);

  useEffect(() => {
    setNarrationCurrentTime(0);
    setNarrationDurationSec(null);
    setNarrationScrubMax(1);
  }, [reportId, report?.audio_id]);

  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    loadReport();
    loadLinks();
  }, [reportId]);
  /* eslint-enable react-hooks/exhaustive-deps */

  const loadReport = async () => {
    try {
      const response = await authFetch(`${BACKEND_URL}/reports/${reportId}`);
      const data = await response.json();
      setReport(data);
    } catch (error) {
      console.error('Error loading report:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadLinks = async () => {
    try {
      const response = await authFetch(`${BACKEND_URL}/reports/${reportId}/links`);
      const data = await response.json();
      setLinks(data);
    } catch (error) {
      console.error('Error loading links:', error);
    }
  };

  const handleGenerateSummary = async () => {
    setGenerating(true);
    try {
      const response = await authFetch(`${BACKEND_URL}/reports/${reportId}/generate-summary`, { method: 'POST' });
      if (response.ok) {
        loadReport();
      } else {
        const data = await response.json().catch(() => ({}));
        alert(data.detail || `Failed to generate summary (${response.status})`);
      }
    } catch (error) {
      console.error('Error generating summary:', error);
      alert('Failed to generate summary');
    } finally {
      setGenerating(false);
    }
  };

  const handleReextractText = async () => {
    setReextracting(true);
    try {
      const response = await authFetch(`${BACKEND_URL}/reports/${reportId}/reextract-text`, {
        method: 'POST',
      });
      const data = await response.json().catch(() => ({}));
      if (response.ok) {
        alert(data.message || 'PDF text updated with page markers.');
      } else {
        alert(typeof data.detail === 'string' ? data.detail : 'Failed to refresh PDF text');
      }
    } catch (error) {
      console.error('Error re-extracting PDF text:', error);
      alert('Failed to refresh PDF text');
    } finally {
      setReextracting(false);
    }
  };

  const handleCreateLink = async () => {
    setCreatingLink(true);
    try {
      const response = await authFetch(`${BACKEND_URL}/reports/${reportId}/create-link`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ expires_in_days: 30 })
      });
      if (response.ok) {
        loadLinks();
      } else {
        alert('Failed to create link');
      }
    } catch (error) {
      console.error('Error creating link:', error);
      alert('Failed to create link');
    } finally {
      setCreatingLink(false);
    }
  };

  const copyLink = (linkId) => {
    const url = `${window.location.origin}/briefing/${linkId}`;
    navigator.clipboard.writeText(url);
    setCopiedLinkId(linkId);
    setTimeout(() => setCopiedLinkId(null), 2000);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
      </div>
    );
  }

  if (!report) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center">
          <FileText className="w-16 h-16 text-slate-300 mx-auto mb-4" />
          <p className="text-slate-600">Report not found</p>
          <button onClick={() => navigate('/dashboard')} className="btn-primary mt-4">
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="glass-surface sticky top-0 z-40 border-b border-slate-200/60">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center space-x-4">
            <button
              onClick={() => navigate('/dashboard')}
              className="p-2 hover:bg-slate-100 rounded-lg transition"
            >
              <ChevronLeft className="w-5 h-5 text-slate-600" />
            </button>
            <div className="flex-1 min-w-0">
              <h1 className="text-xl font-bold text-slate-900 truncate">{report.title}</h1>
              <p className="text-sm text-slate-500">
                Uploaded on {new Date(report.created_at).toLocaleDateString()}
              </p>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">
        {/* Generate Summary Section */}
        {!report.summary ? (
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="glass-card p-8 text-center"
          >
            <div className="w-16 h-16 bg-primary-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <Sparkles className="w-8 h-8 text-primary-600" />
            </div>
            <h2 className="text-2xl font-bold text-slate-900 mb-2">Generate AI Briefing</h2>
            <p className="text-slate-600 mb-6 max-w-md mx-auto">
              Create an AI-powered executive summary and voice narration for your report
            </p>
            <button
              onClick={handleGenerateSummary}
              data-testid="generate-summary-btn"
              disabled={generating}
              className="btn-primary flex items-center space-x-2 mx-auto"
            >
              {generating ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  <span>Generating...</span>
                </>
              ) : (
                <>
                  <Sparkles className="w-5 h-5" />
                  <span>Generate Summary & Audio</span>
                </>
              )}
            </button>
          </motion.div>
        ) : (
          <>
            <div
              className="flex gap-3 rounded-xl border border-primary-200/80 bg-primary-50/50 px-4 py-3 text-sm text-slate-700"
              role="status"
            >
              <Info className="w-5 h-5 flex-shrink-0 text-primary-600 mt-0.5" aria-hidden />
              <p>
                {links.length === 0 ? (
                  <>
                    The summary and voice narration below mirror what your audience will read and hear.{' '}
                    <strong className="font-semibold text-slate-800">Create a share link</strong>, then open it to
                    see the full briefing page (PDF viewer and Q&A).
                  </>
                ) : (
                  <>
                    The summary and voice narration below mirror what your audience will read and hear.{' '}
                    <strong className="font-semibold text-slate-800">Open a share link</strong> to see the full
                    briefing page (PDF viewer and Q&A).
                  </>
                )}
              </p>
            </div>

            {/* Summary Card */}
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="glass-card p-6"
            >
              <div className="flex flex-wrap items-start justify-between gap-2 mb-4">
                <h2 className="text-xl font-bold text-slate-900 flex flex-wrap items-center gap-x-2 gap-y-1">
                  <FileText className="w-5 h-5 text-primary-600" />
                  <span>Executive Summary</span>
                  <span className="inline-flex items-center rounded-full border border-primary-200 bg-primary-50/50 px-2.5 py-0.5 text-xs font-medium text-primary-700">
                    Preview
                  </span>
                </h2>
                <div className="flex items-center space-x-2 text-emerald-600 text-sm font-medium">
                  <CheckCircle2 className="w-4 h-4" />
                  <span>AI Generated</span>
                </div>
              </div>
              <div className="prose prose-slate max-w-none">
                <p className="text-slate-700 whitespace-pre-wrap leading-relaxed">
                  {stripExecutiveSummaryArtifacts(stripMarkdownBold(report.summary))}
                </p>
              </div>
            </motion.div>

            <div className="rounded-xl border border-slate-200/80 bg-slate-50/60 px-4 py-3 flex flex-wrap items-center justify-between gap-3">
              <p className="text-sm text-slate-600 max-w-xl">
                Shared Q&amp;A answers cite PDF page numbers when the stored text includes page tags. If yours never
                mention pages, refresh once from your uploaded PDF (no need to re-upload).
              </p>
              <button
                type="button"
                onClick={handleReextractText}
                disabled={reextracting}
                data-testid="reextract-pdf-text-btn"
                className="flex items-center space-x-2 px-3 py-2 text-sm font-medium text-primary-700 bg-white border border-primary-200 rounded-lg hover:bg-primary-50 transition disabled:opacity-50"
              >
                {reextracting ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <RefreshCw className="w-4 h-4" />
                )}
                <span>Refresh for page citations</span>
              </button>
            </div>

            {/* Audio Player */}
            {report.audio_id && (
              <motion.div 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
                className="glass-card p-6"
              >
                <div className="flex items-center space-x-3 mb-4">
                  <div className="w-10 h-10 bg-primary-100 rounded-xl flex items-center justify-center">
                    <Volume2 className="w-5 h-5 text-primary-600" />
                  </div>
                  <div className="min-w-0">
                    <h3 className="font-semibold text-slate-900 flex flex-wrap items-center gap-x-2 gap-y-1">
                      <span>Voice Narration</span>
                      <span className="inline-flex items-center rounded-full border border-primary-200 bg-primary-50/50 px-2.5 py-0.5 text-xs font-medium text-primary-700">
                        Preview
                      </span>
                    </h3>
                    <p className="text-sm text-slate-500">
                      Same AI narration your audience hears on the shared briefing link
                      {formatNarrationMinutes(narrationDurationSec) && (
                        <span className="text-slate-400"> ({formatNarrationMinutes(narrationDurationSec)} min)</span>
                      )}
                      .
                    </p>
                  </div>
                </div>
                <div className="flex flex-wrap items-center gap-3 mb-3">
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-slate-500 whitespace-nowrap">Speed</span>
                    <select
                      value={narrationPlaybackRate}
                      onChange={(e) => setNarrationPlaybackRate(parseFloat(e.target.value))}
                      className="text-sm bg-white/60 border border-slate-200 rounded-lg px-3 py-2"
                      data-testid="narration-speed-select"
                    >
                      <option value={0.75}>0.75x</option>
                      <option value={1}>1x</option>
                      <option value={1.25}>1.25x</option>
                      <option value={1.5}>1.5x</option>
                      <option value={1.75}>1.75x</option>
                      <option value={2}>2x</option>
                    </select>
                  </div>
                </div>

                <div className="flex items-center gap-3">
                  <button
                    type="button"
                    onClick={() => {
                      const el = narrationAudioRef.current;
                      if (!el) return;
                      el.playbackRate = narrationPlaybackRate;
                      if (el.paused) el.play();
                      else el.pause();
                    }}
                    className="flex-shrink-0 w-11 h-11 rounded-xl bg-primary-600 text-white flex items-center justify-center shadow-md shadow-primary-500/25 hover:bg-primary-700 transition"
                    aria-label={narrationIsPlaying ? 'Pause narration' : 'Play narration'}
                    data-testid="narration-play-pause"
                  >
                    {narrationIsPlaying ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5 ml-0.5" />}
                  </button>
                  <div className="flex-1 min-w-0 space-y-1">
                    <input
                      type="range"
                      className="audio-scrub w-full"
                      min={0}
                      max={Math.max(narrationScrubMax, 0.001)}
                      step={0.1}
                      value={Math.min(Math.max(0, narrationCurrentTime), Math.max(narrationScrubMax, 0.001))}
                      onPointerDown={() => {
                        narrationSeekingRef.current = true;
                      }}
                      onPointerUp={() => {
                        narrationSeekingRef.current = false;
                      }}
                      onPointerCancel={() => {
                        narrationSeekingRef.current = false;
                      }}
                      onChange={(e) => {
                        const el = narrationAudioRef.current;
                        const t = parseFloat(e.target.value);
                        if (el && Number.isFinite(t)) el.currentTime = t;
                        setNarrationCurrentTime(Number.isFinite(t) ? t : 0);
                      }}
                      onInput={(e) => {
                        const el = narrationAudioRef.current;
                        const t = parseFloat(e.target.value);
                        if (el && Number.isFinite(t)) el.currentTime = t;
                        setNarrationCurrentTime(Number.isFinite(t) ? t : 0);
                      }}
                      aria-label="Narration playback position"
                      data-testid="narration-progress"
                    />
                    <div className="flex justify-between text-xs text-slate-500 tabular-nums">
                      <span>{formatAudioClock(narrationCurrentTime)}</span>
                      <span>
                        {narrationDurationSec
                          ? formatAudioClock(narrationDurationSec)
                          : narrationScrubMax > 1.01 || narrationCurrentTime > 0.5
                            ? `~${formatAudioClock(narrationScrubMax)}`
                            : '—'}
                      </span>
                    </div>
                  </div>
                </div>

                <audio
                  ref={narrationAudioRef}
                  preload="auto"
                  className="hidden"
                  data-testid="summary-audio-player"
                  onLoadedMetadata={(e) => {
                    const el = e.currentTarget;
                    const d = readMediaDurationSeconds(el);
                    if (d) {
                      setNarrationDurationSec(d);
                      setNarrationScrubMax(d);
                    } else {
                      setNarrationScrubMax(computeScrubMaxSeconds(el, el.currentTime || 0, null));
                    }
                    setNarrationCurrentTime(el.currentTime || 0);
                  }}
                  onDurationChange={(e) => {
                    const el = e.currentTarget;
                    const d = readMediaDurationSeconds(el);
                    if (d) {
                      setNarrationDurationSec(d);
                      setNarrationScrubMax(d);
                    }
                  }}
                  onProgress={(e) => {
                    const el = e.currentTarget;
                    const d = readMediaDurationSeconds(el);
                    if (d) {
                      setNarrationDurationSec(d);
                      setNarrationScrubMax(d);
                    } else {
                      setNarrationScrubMax((prev) =>
                        Math.max(prev, computeScrubMaxSeconds(el, el.currentTime, null))
                      );
                    }
                  }}
                  onTimeUpdate={(e) => {
                    if (narrationSeekingRef.current) return;
                    const el = e.currentTarget;
                    setNarrationCurrentTime(el.currentTime);
                    const d = readMediaDurationSeconds(el);
                    if (d) {
                      setNarrationDurationSec(d);
                      setNarrationScrubMax(d);
                    } else {
                      setNarrationScrubMax((prev) =>
                        Math.max(prev, computeScrubMaxSeconds(el, el.currentTime, null))
                      );
                    }
                  }}
                  onSeeked={(e) => {
                    setNarrationCurrentTime(e.currentTarget.currentTime);
                  }}
                  onEnded={(e) => {
                    narrationSeekingRef.current = false;
                    const el = e.currentTarget;
                    const d = readMediaDurationSeconds(el);
                    if (d) {
                      setNarrationDurationSec(d);
                      setNarrationScrubMax(d);
                    }
                    setNarrationCurrentTime(d ?? el.currentTime);
                  }}
                  onPlay={() => setNarrationIsPlaying(true)}
                  onPause={() => setNarrationIsPlaying(false)}
                >
                  <source src={`${BACKEND_URL}/audio/${report.audio_id}`} type="audio/mpeg" />
                </audio>
              </motion.div>
            )}

            {/* Share Links */}
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="glass-card p-6"
            >
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-emerald-100 rounded-xl flex items-center justify-center">
                    <LinkIcon className="w-5 h-5 text-emerald-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-900">Share Links</h3>
                    <p className="text-sm text-slate-500">
                      Open a link in a new tab to preview the full experience your recipients get
                    </p>
                  </div>
                </div>
                <button
                  onClick={handleCreateLink}
                  data-testid="create-link-btn"
                  disabled={creatingLink}
                  className="btn-primary flex items-center space-x-2"
                >
                  {creatingLink ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Plus className="w-4 h-4" />
                  )}
                  <span>Create Link</span>
                </button>
              </div>

              {links.length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  <LinkIcon className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                  <p className="max-w-md mx-auto">
                    No share links yet. Create one to share this briefing, then open it to preview the recipient
                    view.
                  </p>
                </div>
              ) : (
                <div className="space-y-3">
                  {links.map((link) => (
                    <div
                      key={link.link_id}
                      data-testid={`link-card-${link.link_id}`}
                      className="flex items-center justify-between gap-2 p-4 bg-slate-50 rounded-xl border border-slate-200"
                    >
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-mono text-slate-600 truncate">
                          {window.location.origin}/briefing/{link.link_id}
                        </p>
                        <div className="flex items-center space-x-4 mt-1 text-xs text-slate-500">
                          <span className="flex items-center space-x-1">
                            <Eye className="w-3 h-3" />
                            <span>{link.access_count} views</span>
                          </span>
                          {link.expires_at && (
                            <span className="flex items-center space-x-1">
                              <Clock className="w-3 h-3" />
                              <span>Expires {new Date(link.expires_at).toLocaleDateString()}</span>
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center flex-shrink-0">
                        <button
                          type="button"
                          onClick={() =>
                            window.open(
                              `${window.location.origin}/briefing/${link.link_id}`,
                              '_blank',
                              'noopener,noreferrer'
                            )
                          }
                          data-testid={`open-briefing-link-${link.link_id}`}
                          className="px-3 py-2 text-sm font-medium text-primary-700 bg-white border border-primary-200 hover:bg-primary-50 rounded-lg transition flex items-center space-x-1.5"
                          aria-label="View briefing as recipient"
                        >
                          <ExternalLink className="w-4 h-4" />
                          <span>Open</span>
                        </button>
                        <button
                          onClick={() => copyLink(link.link_id)}
                          data-testid={`copy-link-btn-${link.link_id}`}
                          className="ml-1 p-2 hover:bg-slate-200 rounded-lg transition flex items-center space-x-1"
                          aria-label="Copy briefing link"
                        >
                          {copiedLinkId === link.link_id ? (
                            <Check className="w-5 h-5 text-emerald-600" />
                          ) : (
                            <Copy className="w-5 h-5 text-slate-500" />
                          )}
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </motion.div>
          </>
        )}
      </main>
    </div>
  );
}

// ==================== Public Briefing Page with Voice Agent + PDF ====================
function BriefingPage() {
  const { linkId } = useLocation().pathname.match(/\/briefing\/(?<linkId>[^/]+)/)?.groups || {};
  const [briefing, setBriefing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [question, setQuestion] = useState('');
  const [messages, setMessages] = useState([]);
  const [conversationId, setConversationId] = useState(null);
  const [asking, setAsking] = useState(false);
  const [emailDraft, setEmailDraft] = useState(null);
  const [isVoiceMode, setIsVoiceMode] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const chatEndRef = useRef(null);
  const audioRef = useRef(null);
  const summaryAudioRef = useRef(null);
  const [playbackRate, setPlaybackRate] = useState(1);
  const [summaryDurationSec, setSummaryDurationSec] = useState(null);
  const [summaryScrubMax, setSummaryScrubMax] = useState(1);
  const [summaryCurrentTime, setSummaryCurrentTime] = useState(0);
  const summarySeekingRef = useRef(false);
  const [assistantAudioDurationSec, setAssistantAudioDurationSec] = useState(null);
  const [assistantAudioSrc, setAssistantAudioSrc] = useState(null);
  const [dictationRecording, setDictationRecording] = useState(false);
  const [dictationBusy, setDictationBusy] = useState(false);
  const [dictationStatus, setDictationStatus] = useState('');
  const briefingDictationRef = useRef({
    ctx: null,
    processor: null,
    source: null,
    gain: null,
    stream: null,
    chunks: [],
  });

  const safeDurationSeconds = (seconds) => {
    return Number.isFinite(seconds) && seconds > 0 ? seconds : null;
  };

  const formatNarrationMinutes = (seconds) => {
    const safe = safeDurationSeconds(seconds);
    if (!safe) return null;
    return Math.max(1, Math.round(safe / 60));
  };

  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    loadBriefing();
  }, [linkId]);
  /* eslint-enable react-hooks/exhaustive-deps */

  useEffect(() => {
    if (summaryAudioRef.current) {
      summaryAudioRef.current.playbackRate = playbackRate;
    }
    if (audioRef.current) {
      audioRef.current.playbackRate = playbackRate;
    }
  }, [playbackRate]);

  useEffect(() => {
    setSummaryCurrentTime(0);
    setSummaryDurationSec(null);
    setSummaryScrubMax(1);
  }, [linkId, briefing?.audio_url]);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, asking]);

  useEffect(() => {
    return () => {
      const r = briefingDictationRef.current;
      if (r.ctx) {
        try {
          r.processor?.disconnect();
          r.source?.disconnect();
          r.gain?.disconnect();
          r.stream?.getTracks().forEach((t) => t.stop());
          void r.ctx.close();
        } catch (_) {}
        briefingDictationRef.current = {
          ctx: null,
          processor: null,
          source: null,
          gain: null,
          stream: null,
          chunks: [],
        };
      }
    };
  }, []);

  const stopBriefingDictationCapture = () => {
    const r = briefingDictationRef.current;
    if (!r.ctx) return null;
    const { chunks, ctx, processor, source, gain, stream } = r;
    const sampleRate = ctx.sampleRate;
    try {
      processor.disconnect();
      source.disconnect();
      gain.disconnect();
      stream.getTracks().forEach((t) => t.stop());
    } catch (_) {}
    void ctx.close();
    briefingDictationRef.current = {
      ctx: null,
      processor: null,
      source: null,
      gain: null,
      stream: null,
      chunks: [],
    };
    return { float32: mergeFloat32Chunks(chunks), sampleRate };
  };

  const toggleBriefingDictation = async () => {
    if (dictationBusy || asking) return;

    if (dictationRecording) {
      setDictationRecording(false);
      const captured = stopBriefingDictationCapture();
      setDictationStatus('');
      if (!captured || captured.float32.length === 0) {
        return;
      }
      setDictationBusy(true);
      setDictationStatus('Transcribing…');
      try {
        const text = await transcribePcm(captured.float32, captured.sampleRate, (data) => {
          if (data.status === 'initiate') {
            setDictationStatus('Getting ready…');
          } else if (data.status === 'progress' && typeof data.progress === 'number') {
            setDictationStatus(`Getting ready… ${Math.min(100, Math.round(data.progress))}%`);
          } else if (data.status === 'done') {
            setDictationStatus('Transcribing…');
          }
        });
        if (text) {
          setQuestion((q) => (q.trim() ? `${q.trimEnd()} ` : '') + text);
        }
      } catch (err) {
        console.error(err);
        alert(err?.message || 'Could not transcribe audio');
      } finally {
        setDictationBusy(false);
        setDictationStatus('');
      }
      return;
    }

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const ctx = new AudioContext();
      await ctx.resume();
      const source = ctx.createMediaStreamSource(stream);
      const processor = ctx.createScriptProcessor(4096, 1, 1);
      const chunks = [];
      processor.onaudioprocess = (e) => {
        chunks.push(new Float32Array(e.inputBuffer.getChannelData(0)));
      };
      const gain = ctx.createGain();
      gain.gain.value = 0;
      source.connect(processor);
      processor.connect(gain);
      gain.connect(ctx.destination);
      briefingDictationRef.current = { ctx, processor, source, gain, stream, chunks };
      setDictationRecording(true);
      setDictationStatus('Listening… tap the mic again to transcribe');
    } catch (err) {
      console.error(err);
      alert('Microphone access is required for voice dictation');
    }
  };

  const loadBriefing = async () => {
    try {
      const response = await authFetch(`${BACKEND_URL}/briefing/${linkId}`);
      if (!response.ok) {
        if (response.status === 410) alert('This link has expired');
        else if (response.status === 404) alert('Link not found');
        return;
      }
      const data = await response.json();
      setBriefing(data);
    } catch (error) {
      console.error('Error loading briefing:', error);
      alert('Failed to load briefing');
    } finally {
      setLoading(false);
    }
  };

  const handleAskQuestion = async (e) => {
    e.preventDefault();
    if (!question.trim()) return;

    const userMessage = { role: 'user', content: question, timestamp: new Date() };
    setMessages([...messages, userMessage]);
    setQuestion('');
    setAsking(true);
    setEmailDraft(null);

    try {
      const response = await authFetch(`${BACKEND_URL}/briefing/${linkId}/ask`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ question: question, conversation_id: conversationId })
      });

      const data = await response.json();
      if (!conversationId) setConversationId(data.conversation_id);

      const assistantMessage = { role: 'assistant', content: data.answer, timestamp: new Date() };
      setMessages(prev => [...prev, assistantMessage]);

      if (data.is_out_of_scope && data.email_draft) setEmailDraft(data.email_draft);
    } catch (error) {
      console.error('Error asking question:', error);
      alert('Failed to get answer');
    } finally {
      setAsking(false);
    }
  };

  const toggleSummaryAudio = () => {
    const el = summaryAudioRef.current;
    if (!el) return;
    el.playbackRate = playbackRate;
    if (el.paused) {
      void el.play();
    } else {
      el.pause();
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center">
          <Loader2 className="w-10 h-10 animate-spin text-primary-600 mx-auto mb-4" />
          <p className="text-slate-600">Loading briefing...</p>
        </div>
      </div>
    );
  }

  if (!briefing) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center">
          <AlertCircle className="w-16 h-16 text-slate-300 mx-auto mb-4" />
          <p className="text-slate-600">Briefing not found</p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen bg-slate-50 flex flex-col">
      {/* Header */}
      <header className="glass-surface border-b border-slate-200/60 px-6 py-4 flex-shrink-0">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <div className="w-10 h-10 bg-gradient-to-br from-primary-500 to-primary-700 rounded-xl flex items-center justify-center shadow-lg shadow-primary-500/25">
              <Sparkles className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="text-lg font-bold text-slate-900 line-clamp-1">{briefing.title}</h1>
              <p className="text-sm text-slate-500">Interactive AI Briefing</p>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <div className="inline-flex items-center p-1 rounded-xl bg-slate-100/60 border border-slate-200/70 backdrop-blur-sm">
              <button
                onClick={() => setIsVoiceMode(false)}
                className={`px-4 py-2 rounded-l-xl text-sm font-medium flex items-center space-x-2 transition ${
                  !isVoiceMode
                    ? 'bg-primary-600 text-white shadow-md'
                    : 'bg-transparent text-slate-500 hover:text-slate-700'
                }`}
              >
                <MessageSquare className="w-4 h-4" />
                <span>Text Mode</span>
              </button>
              <button
                onClick={() => setIsVoiceMode(true)}
                className={`px-4 py-2 rounded-r-xl text-sm font-medium flex items-center space-x-2 transition ${
                  isVoiceMode
                    ? 'bg-primary-600 text-white shadow-md'
                    : 'bg-transparent text-slate-500 hover:text-slate-700'
                }`}
              >
                <Mic className="w-4 h-4" />
                <span>Voice Agent Mode</span>
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content - Split View */}
      <div className="flex-1 flex overflow-hidden">
        {/* Left Panel - Voice Agent Interface */}
        <div className="w-full lg:w-2/5 flex flex-col bg-white border-r border-slate-200">
          {/* Summary Audio Player */}
          <div className="p-6 border-b border-slate-100">
            <div className="flex items-center space-x-4">
              <button
                onClick={toggleSummaryAudio}
                disabled={!briefing.audio_url}
                className="w-14 h-14 bg-gradient-to-br from-primary-500 to-primary-600 rounded-2xl flex items-center justify-center text-white shadow-lg shadow-primary-500/30 hover:shadow-primary-500/40 transition disabled:opacity-50"
              >
                {isPlaying ? <Pause className="w-6 h-6" /> : <Play className="w-6 h-6 ml-1" />}
              </button>
              <div className="flex-1">
                <h3 className="font-semibold text-slate-900">Executive Summary</h3>
                <p className="text-sm text-slate-500">
                  {summaryDurationSec
                    ? `${formatNarrationMinutes(summaryDurationSec)}-minute AI narration`
                    : 'AI narration'}
                </p>
                <div className="mt-2 flex items-center space-x-2">
                  <span className="text-xs text-slate-500 whitespace-nowrap">Speed</span>
                  <select
                    value={playbackRate}
                    onChange={(e) => setPlaybackRate(parseFloat(e.target.value))}
                    className="text-xs bg-white/60 border border-slate-200 rounded-lg px-2 py-1"
                    data-testid="briefing-speed-select"
                  >
                    <option value={0.75}>0.75x</option>
                    <option value={1}>1x</option>
                    <option value={1.25}>1.25x</option>
                    <option value={1.5}>1.5x</option>
                    <option value={1.75}>1.75x</option>
                    <option value={2}>2x</option>
                  </select>
                </div>
                <div className="mt-3 space-y-1">
                  <input
                    type="range"
                    className="audio-scrub w-full"
                    min={0}
                    max={Math.max(summaryScrubMax, 0.001)}
                    step={0.1}
                    value={Math.min(Math.max(0, summaryCurrentTime), Math.max(summaryScrubMax, 0.001))}
                    onPointerDown={() => {
                      summarySeekingRef.current = true;
                    }}
                    onPointerUp={() => {
                      summarySeekingRef.current = false;
                    }}
                    onPointerCancel={() => {
                      summarySeekingRef.current = false;
                    }}
                    onChange={(e) => {
                      const el = summaryAudioRef.current;
                      const t = parseFloat(e.target.value);
                      if (el && Number.isFinite(t)) el.currentTime = t;
                      setSummaryCurrentTime(Number.isFinite(t) ? t : 0);
                    }}
                    onInput={(e) => {
                      const el = summaryAudioRef.current;
                      const t = parseFloat(e.target.value);
                      if (el && Number.isFinite(t)) el.currentTime = t;
                      setSummaryCurrentTime(Number.isFinite(t) ? t : 0);
                    }}
                    aria-label="Summary narration position"
                    data-testid="briefing-summary-progress"
                  />
                  <div className="flex justify-between text-xs text-slate-500 tabular-nums">
                    <span>{formatAudioClock(summaryCurrentTime)}</span>
                    <span>
                      {summaryDurationSec
                        ? formatAudioClock(summaryDurationSec)
                        : summaryScrubMax > 1.01 || summaryCurrentTime > 0.5
                          ? `~${formatAudioClock(summaryScrubMax)}`
                          : '—'}
                    </span>
                  </div>
                </div>
              </div>
            </div>
            {briefing.audio_url && (
              <audio 
                ref={summaryAudioRef} 
                src={BACKEND_ORIGIN ? `${BACKEND_ORIGIN}${briefing.audio_url}` : briefing.audio_url}
                onEnded={(e) => {
                  setIsPlaying(false);
                  summarySeekingRef.current = false;
                  const el = e.currentTarget;
                  const d = readMediaDurationSeconds(el);
                  if (d) {
                    setSummaryDurationSec(d);
                    setSummaryScrubMax(d);
                  }
                  setSummaryCurrentTime(d ?? el.currentTime);
                }}
                onPlay={() => setIsPlaying(true)}
                onPause={() => setIsPlaying(false)}
                preload="auto"
                onLoadedMetadata={(e) => {
                  const el = e.currentTarget;
                  const d = readMediaDurationSeconds(el);
                  if (d) {
                    setSummaryDurationSec(d);
                    setSummaryScrubMax(d);
                  } else {
                    setSummaryScrubMax(computeScrubMaxSeconds(el, el.currentTime || 0, null));
                  }
                  setSummaryCurrentTime(el.currentTime || 0);
                }}
                onDurationChange={(e) => {
                  const el = e.currentTarget;
                  const d = readMediaDurationSeconds(el);
                  if (d) {
                    setSummaryDurationSec(d);
                    setSummaryScrubMax(d);
                  }
                }}
                onProgress={(e) => {
                  const el = e.currentTarget;
                  const d = readMediaDurationSeconds(el);
                  if (d) {
                    setSummaryDurationSec(d);
                    setSummaryScrubMax(d);
                  } else {
                    setSummaryScrubMax((prev) =>
                      Math.max(prev, computeScrubMaxSeconds(el, el.currentTime, null))
                    );
                  }
                }}
                onTimeUpdate={(e) => {
                  if (summarySeekingRef.current) return;
                  const el = e.currentTarget;
                  setSummaryCurrentTime(el.currentTime);
                  const d = readMediaDurationSeconds(el);
                  if (d) {
                    setSummaryDurationSec(d);
                    setSummaryScrubMax(d);
                  } else {
                    setSummaryScrubMax((prev) =>
                      Math.max(prev, computeScrubMaxSeconds(el, el.currentTime, null))
                    );
                  }
                }}
                onSeeked={(e) => setSummaryCurrentTime(e.currentTarget.currentTime)}
                data-testid="audio-player"
                className="hidden"
              />
            )}
          </div>

          {isVoiceMode ? (
            <div className="flex-1 overflow-y-auto p-6" data-testid="voice-agent-waitlist-panel">
              <div className="rounded-2xl border border-primary-200/80 bg-gradient-to-b from-primary-50/80 to-white p-6 shadow-sm">
                <div className="flex items-start space-x-3 mb-4">
                  <div className="w-11 h-11 rounded-xl bg-primary-100 flex items-center justify-center flex-shrink-0">
                    <Bot className="w-6 h-6 text-primary-700" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-slate-900">Conversational Voice Agent</h3>
                    <p className="text-sm text-primary-700 font-medium mt-0.5">Coming soon</p>
                  </div>
                </div>
                <p className="text-sm text-slate-600 leading-relaxed mb-4">
                  Full conversational voice Q&A on this briefing will be available to everyone soon. Join the waitlist
                  to get early access when we launch.
                </p>
                <Link
                  to="/waitlist"
                  className="inline-flex items-center justify-center space-x-2 rounded-xl bg-primary-600 text-white px-4 py-2.5 text-sm font-semibold shadow-md shadow-primary-500/25 hover:bg-primary-700 transition"
                  data-testid="briefing-waitlist-link"
                >
                  <span>Join the waitlist</span>
                  <ArrowRight className="w-4 h-4" />
                </Link>
                <div className="mt-6 pt-5 border-t border-slate-200/80">
                  <p className="text-sm text-slate-600 leading-relaxed">
                    <span className="font-semibold text-slate-800">Prefer a hands-free option today?</span> Switch to{' '}
                    <button
                      type="button"
                      onClick={() => setIsVoiceMode(false)}
                      className="text-primary-700 font-semibold underline decoration-primary-300 underline-offset-2 hover:text-primary-800"
                    >
                      Text Mode
                    </button>{' '}
                    and use your keyboard&apos;s <span className="font-medium text-slate-800">voice-to-text</span> (mic /
                    dictation) in the question field on supported devices—most mobile keyboards and many desktops include this
                    for a typing-free experience.
                  </p>
                </div>
              </div>
            </div>
          ) : (
            <>
              {/* Chat Messages */}
              <div className="flex-1 overflow-y-auto p-6 space-y-4" data-testid="chat-container">
                {messages.length === 0 && !asking ? (
                  <div className="text-center py-12">
                    <div className="w-16 h-16 bg-slate-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                      <MessageSquare className="w-8 h-8 text-slate-400" />
                    </div>
                    <p className="text-slate-500 mb-2">No questions yet</p>
                    <p className="text-sm text-slate-400">Ask a question about this report</p>
                  </div>
                ) : (
                  <>
                    {messages.map((msg, idx) => (
                      <motion.div
                        key={idx}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                      >
                        <div
                          className={`max-w-[85%] ${msg.role === 'user' ? 'message-user' : 'message-assistant'}`}
                          data-testid={`message-${msg.role}-${idx}`}
                        >
                          <p className="text-sm leading-relaxed">
                            {msg.role === 'assistant' ? stripMarkdownBold(msg.content) : msg.content}
                          </p>
                          {msg.audio && (
                            <button
                              onClick={() => {
                                if (!audioRef.current) return;
                                audioRef.current.src = msg.audio;
                                setAssistantAudioSrc(msg.audio);
                                setAssistantAudioDurationSec(null);
                                audioRef.current.playbackRate = playbackRate;
                                audioRef.current.play();
                              }}
                              className="mt-2 text-xs flex items-center space-x-1 opacity-80 hover:opacity-100"
                            >
                              <Volume2 className="w-3 h-3" />
                              <span>
                                Play audio
                                {assistantAudioSrc === msg.audio && assistantAudioDurationSec && (
                                  ` (${formatNarrationMinutes(assistantAudioDurationSec)} min)`
                                )}
                              </span>
                            </button>
                          )}
                        </div>
                      </motion.div>
                    ))}
                    {asking && (
                      <motion.div
                        initial={{ opacity: 0, y: 8 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="flex justify-start"
                        aria-live="polite"
                        aria-busy="true"
                      >
                        <div
                          className="max-w-[85%] message-assistant flex items-center gap-2.5 py-1"
                          data-testid="briefing-thinking-indicator"
                        >
                          <Loader2 className="w-4 h-4 animate-spin text-primary-600 flex-shrink-0" aria-hidden />
                          <span className="text-sm text-slate-600 font-medium">Thinking…</span>
                        </div>
                      </motion.div>
                    )}
                  </>
                )}
                <div ref={chatEndRef} />
              </div>

              {/* Email Draft */}
              {emailDraft && (
                <div className="mx-6 mb-4 p-4 bg-blue-50 border border-blue-200 rounded-xl">
                  <h3 className="font-semibold text-blue-900 mb-2 flex items-center space-x-2">
                    <MessageSquare className="w-4 h-4" />
                    <span>Suggested Email</span>
                  </h3>
                  <p className="text-sm text-blue-800 whitespace-pre-wrap">
                    {stripMarkdownBold(emailDraft)}
                  </p>
                </div>
              )}

              {/* Input Area */}
                <div className="p-6 border-t border-slate-100">
                <form onSubmit={handleAskQuestion} className="flex flex-col gap-2">
                  <div className="flex items-stretch gap-2">
                    <input
                      type="text"
                      value={question}
                      onChange={(e) => setQuestion(e.target.value)}
                      data-testid="question-input"
                      placeholder="Ask a question about this report..."
                      disabled={asking || dictationRecording || dictationBusy}
                      className="input-field flex-1 min-w-0"
                    />
                    <button
                      type="button"
                      onClick={toggleBriefingDictation}
                      disabled={asking || dictationBusy}
                      title="Dictate hands-free — runs in your browser"
                      aria-label={
                        dictationRecording
                          ? 'Stop recording and transcribe'
                          : 'Dictate with microphone'
                      }
                      aria-pressed={dictationRecording}
                      data-testid="briefing-dictation-mic"
                      className={`flex-shrink-0 w-11 h-11 rounded-xl flex items-center justify-center transition shadow-md border ${
                        dictationRecording
                          ? 'bg-red-500 border-red-600 text-white shadow-red-500/25 animate-pulse'
                          : 'bg-slate-100 border-slate-200 text-primary-700 hover:bg-primary-50 hover:border-primary-200'
                      } disabled:opacity-50 disabled:cursor-not-allowed`}
                    >
                      {dictationBusy ? (
                        <Loader2 className="w-5 h-5 animate-spin text-primary-600" />
                      ) : (
                        <Mic className="w-5 h-5" />
                      )}
                    </button>
                    <button
                      type="submit"
                      data-testid="ask-question-btn"
                      disabled={asking || !question.trim() || dictationRecording || dictationBusy}
                      className="btn-primary px-4 flex-shrink-0"
                    >
                      {asking ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />}
                    </button>
                  </div>
                  {(dictationRecording || dictationBusy) && (
                    <p className="text-xs text-slate-500 leading-snug" role="status">
                      {dictationStatus ||
                        (dictationRecording ? 'Listening… tap the mic again to transcribe' : '')}
                    </p>
                  )}
                  {!dictationRecording && !dictationBusy && (
                    <p className="text-xs text-slate-400 leading-snug">
                      Tip: use the mic for hands-free typing. Whisper runs locally in your browser.
                    </p>
                  )}
                </form>
              </div>
            </>
          )}
        </div>

        {/* Right Panel - PDF Viewer */}
        <div className="hidden lg:flex flex-1 flex-col bg-slate-100 p-6 overflow-hidden">
          <div className="glass-card p-6 h-full flex flex-col">
            <div className="flex items-center space-x-3 mb-4">
              <div className="w-10 h-10 bg-primary-100 rounded-xl flex items-center justify-center">
                <FileText className="w-5 h-5 text-primary-600" />
              </div>
              <div className="min-w-0">
                <h2 className="text-lg font-bold text-slate-900 truncate">{briefing.title}</h2>
                <p className="text-sm text-slate-500">
                  Created {new Date(briefing.created_at).toLocaleDateString()}
                </p>
              </div>
            </div>

            <div className="flex-1 overflow-hidden rounded-xl border border-slate-200 bg-white">
              {briefing.pdf_url ? (
                <iframe
                  src={BACKEND_ORIGIN ? `${BACKEND_ORIGIN}${briefing.pdf_url}` : briefing.pdf_url}
                  className="w-full h-full"
                  title="PDF viewer"
                />
              ) : (
                <div className="h-full overflow-y-auto p-4">
                  <div className="prose prose-slate max-w-none">
                    <h3 className="text-lg font-semibold text-slate-900 mb-3">Summary</h3>
                    <p className="text-slate-700 whitespace-pre-wrap leading-relaxed">
                      {stripExecutiveSummaryArtifacts(stripMarkdownBold(briefing.summary))}
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Hidden audio element */}
      <audio
        ref={audioRef}
        className="hidden"
        onLoadedMetadata={(e) => setAssistantAudioDurationSec(safeDurationSeconds(e.currentTarget.duration))}
      />
    </div>
  );
}

// ==================== Voice Chat Mode Page ====================
function VoiceChatModePage() {
  const { linkId } = useLocation().pathname.match(/\/briefing\/(?<linkId>[^/]+)\/voice/)?.groups || {};
  const navigate = useNavigate();
  const [briefing, setBriefing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [messages, setMessages] = useState([]);
  const [conversationId, setConversationId] = useState(null);
  const [isRecording, setIsRecording] = useState(false);
  const [mediaRecorder, setMediaRecorder] = useState(null);
  const [processing, setProcessing] = useState(false);
  const audioRef = useRef(null);

  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    loadBriefing();
  }, [linkId]);
  /* eslint-enable react-hooks/exhaustive-deps */

  const loadBriefing = async () => {
    try {
      const response = await authFetch(`${BACKEND_URL}/briefing/${linkId}`);
      if (response.ok) {
        const data = await response.json();
        setBriefing(data);
      }
    } catch (error) {
      console.error('Error loading briefing:', error);
    } finally {
      setLoading(false);
    }
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const recorder = new MediaRecorder(stream);
      const audioChunks = [];

      recorder.ondataavailable = (event) => audioChunks.push(event.data);
      recorder.onstop = async () => {
        const audioBlob = new Blob(audioChunks, { type: 'audio/webm' });
        await handleVoiceQuestion(audioBlob);
        stream.getTracks().forEach(track => track.stop());
      };

      recorder.start();
      setMediaRecorder(recorder);
      setIsRecording(true);
    } catch (error) {
      console.error('Error starting recording:', error);
      alert('Failed to access microphone');
    }
  };

  const stopRecording = () => {
    if (mediaRecorder) {
      mediaRecorder.stop();
      setIsRecording(false);
    }
  };

  const handleVoiceQuestion = async (audioBlob) => {
    setProcessing(true);

    try {
      const formData = new FormData();
      formData.append('audio_file', audioBlob, 'question.webm');
      if (conversationId) formData.append('conversation_id', conversationId);

      const response = await authFetch(`${BACKEND_URL}/briefing/${linkId}/voice-question`, {
        method: 'POST',
        body: formData
      });

      const data = await response.json();
      if (!conversationId) setConversationId(data.conversation_id);

      const userMessage = { role: 'user', content: data.question_text, timestamp: new Date() };
      const assistantMessage = { role: 'assistant', content: data.answer_text, timestamp: new Date(), audio: data.answer_audio };

      setMessages(prev => [...prev, userMessage, assistantMessage]);

      if (data.answer_audio && audioRef.current) {
        audioRef.current.src = data.answer_audio;
        audioRef.current.play();
      }
    } catch (error) {
      console.error('Error processing voice question:', error);
      alert('Failed to process voice question');
    } finally {
      setProcessing(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary-600 to-emerald-600">
        <Loader2 className="w-10 h-10 animate-spin text-white" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-600 via-primary-700 to-emerald-700">
      {/* Header */}
      <header className="bg-white/10 backdrop-blur-xl border-b border-white/20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex justify-between items-center">
            <button
              onClick={() => navigate(`/briefing/${linkId}`)}
              className="text-white/80 hover:text-white flex items-center space-x-2 transition"
            >
              <ChevronLeft className="w-5 h-5" />
              <span>Back to Text Mode</span>
            </button>
            <div className="flex items-center space-x-2 text-white">
              <Mic className="w-5 h-5" />
              <span className="font-semibold">Voice Agent Mode</span>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Title Card */}
        <div className="bg-white/10 backdrop-blur-xl rounded-2xl p-6 mb-6 text-white border border-white/20">
          <h2 className="text-2xl font-bold mb-2">{briefing?.title}</h2>
          <p className="text-sm opacity-80">Speak your questions and hear AI responses</p>
        </div>

        {/* Conversation History */}
        <div className="bg-white/10 backdrop-blur-xl rounded-2xl p-6 mb-6 max-h-96 overflow-y-auto border border-white/20">
          {messages.length === 0 ? (
            <p className="text-white/60 text-center py-8">Tap the microphone to start speaking</p>
          ) : (
            <div className="space-y-4">
              {messages.map((msg, idx) => (
                <div key={idx} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                  <div className={`max-w-md px-4 py-3 rounded-2xl ${
                    msg.role === 'user' 
                      ? 'bg-white/20 text-white rounded-br-md' 
                      : 'bg-white/30 text-white rounded-bl-md'
                  }`}>
                    <p className="text-sm">
                      {msg.role === 'assistant' ? stripMarkdownBold(msg.content) : msg.content}
                    </p>
                    {msg.audio && (
                      <button
                        onClick={() => {
                          audioRef.current.src = msg.audio;
                          audioRef.current.play();
                        }}
                        className="mt-2 text-xs flex items-center space-x-1 opacity-80 hover:opacity-100"
                      >
                        <Volume2 className="w-3 h-3" />
                        <span>Replay</span>
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Voice Control */}
        <div className="flex flex-col items-center">
          <button
            onClick={isRecording ? stopRecording : startRecording}
            disabled={processing}
            className={`w-24 h-24 rounded-full flex items-center justify-center transition-all relative ${
              isRecording
                ? 'bg-red-500 scale-110'
                : processing
                ? 'bg-white/20'
                : 'bg-white hover:bg-white/90 shadow-2xl'
            }`}
          >
            {isRecording && (
              <span className="absolute inset-0 rounded-full bg-red-400 animate-ping opacity-50" />
            )}
            {processing ? (
              <Loader2 className="w-10 h-10 text-white animate-spin" />
            ) : (
              <Mic className={`w-10 h-10 ${isRecording ? 'text-white' : 'text-primary-600'}`} />
            )}
          </button>
          <p className="text-white/80 mt-4 text-sm">
            {processing ? 'Processing your question...' : isRecording ? 'Recording... Tap to stop' : 'Tap to speak'}
          </p>
        </div>
      </main>

      <audio ref={audioRef} className="hidden" />
    </div>
  );
}

// ==================== App Router ====================
function AppRouter() {
  const location = useLocation();

  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/waitlist" element={<WaitlistPage />} />
      <Route path="/waitlist-admin" element={<WaitlistAdminPage />} />
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <DashboardPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/report/:reportId"
        element={
          <ProtectedRoute>
            <ReportDetailPage />
          </ProtectedRoute>
        }
      />
      <Route path="/briefing/:linkId" element={<BriefingPage />} />
      <Route path="/briefing/:linkId/voice" element={<VoiceChatModePage />} />
    </Routes>
  );
}

// ==================== Main App ====================
function App() {
  return (
    <Router>
      <AppRouter />
    </Router>
  );
}

export default App;
